import { useState } from "react"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowUpRight, ChevronRight, Plus, ShieldCheck, TriangleAlert } from "lucide-react"
import { toast } from "sonner"
import type { ModerationPolicy } from "@vienna/contracts"
import { CATEGORY_LABELS } from "@/components/comments/assessment-labels"
import { AutoHeading, DotBadge, Note } from "@/components/automations/primitives"
import { AutomationsNav } from "@/components/automations/section-nav"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Progress } from "@/components/ui/progress"
import { Switch } from "@/components/ui/switch"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { api, ApiError } from "@/lib/api"
import { formatDateTime } from "@/lib/format"
import { channelsQuery, moderationQuery } from "@/lib/queries"
import { useCan, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/automations/moderation/")({
  loader: ({ context, params }) => Promise.all([context.queryClient.ensureQueryData(moderationQuery(params.workspace)), context.queryClient.ensureQueryData(channelsQuery(params.workspace))]),
  component: Moderation,
})

const STATUS: Record<string, string> = { draft: "Draft", enabled: "Enabled", paused: "Paused", paused_access_missing: "Paused · access missing" }
const SUGGESTION: Record<string, string> = { hide: "Would hide", review: "Would hold for review", route: "Would route to support", signal: "Signal", allow: "Would allow" }

function SectionHead({ title, description, children }: { title: string; description: string; children?: React.ReactNode }) {
  return (
    <div className="auto-section-head">
      <div>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      {children}
    </div>
  )
}

/** Moderation under Automations: policy and kill switch, phrase rules in priority order, and what the classifier would have done. */
function Moderation() {
  const { workspace } = useWorkspaceContext()
  const can = useCan()
  const queryClient = useQueryClient()
  const { rules, activity, policy, suggestions } = useSuspenseQuery(moderationQuery(workspace.slug)).data
  const channels = useSuspenseQuery(channelsQuery(workspace.slug)).data
  const params = { workspace: workspace.slug }
  const blocked = channels.connections.filter((c) => c.status !== "disconnected" && c.capabilities.read_comments !== "available")
  const [text, setText] = useState(policy.text)
  const [budget, setBudget] = useState(String(policy.monthlyTokenBudget))
  const [seen, setSeen] = useState(policy.revision)
  if (seen !== policy.revision) {
    setSeen(policy.revision)
    setText(policy.text)
    setBudget(String(policy.monthlyTokenBudget))
  }
  const save = useMutation({
    mutationFn: (input: Partial<Pick<ModerationPolicy, "text" | "paused" | "monthlyTokenBudget">>) => api<ModerationPolicy>(`/w/${workspace.slug}/moderation/policy`, { method: "PUT", body: JSON.stringify({ text: input.text ?? text, paused: input.paused ?? policy.paused, monthlyTokenBudget: input.monthlyTokenBudget ?? (Number(budget) || policy.monthlyTokenBudget) }) }),
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "moderation"] })
      toast.success(saved.paused ? "All moderation paused" : `Policy saved · revision ${saved.revision}`)
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not save the policy"),
  })
  const manage = can("manage_support")
  const usedPct = policy.monthlyTokenBudget ? Math.min(100, Math.round((policy.tokensUsedThisMonth / policy.monthlyTokenBudget) * 100)) : 0
  const dirty = text !== policy.text || Number(budget) !== policy.monthlyTokenBudget
  const providerLine = policy.provider === "anthropic" ? `Assessments run on ${policy.model}.` : policy.provider === "sandbox" ? "Assessments run on the deterministic sandbox classifier." : "Assessments are off on this deployment until an API key is configured."
  const commentLink = (commentId: string) => ({ to: "/w/$workspace/inbox" as const, params, search: { c: commentId, k: "comment" as const, type: "comments" as const, view: "all" as const, visibility: "any" as const } })

  return (
    <div className="page-wrap auto-page">
      <AutoHeading title="Moderation" description="Rules act on comments the moment they arrive. The classifier reads every comment too and, for now, only suggests.">
        {manage && (
          <Button asChild>
            <Link to="/w/$workspace/automations/moderation/$ruleId" params={{ ...params, ruleId: "new" }}>
              <Plus />
              Create rule
            </Link>
          </Button>
        )}
      </AutoHeading>
      <AutomationsNav workspace={workspace.slug} />

      {policy.paused && (
        <div className="auto-validation-banner has-warning mb-6">
          <TriangleAlert className="size-5" />
          <div className="flex-1">
            <h2>All moderation is paused</h2>
            <p>No rule runs and no comment is assessed until an admin resumes it.</p>
          </div>
          {manage && (
            <Button variant="outline" onClick={() => save.mutate({ paused: false })} disabled={save.isPending}>
              Resume
            </Button>
          )}
        </div>
      )}
      {blocked.map((c) => (
        <div key={c.id} className="auto-validation-banner has-warning mb-6">
          <TriangleAlert className="size-5" />
          <div className="flex-1">
            <h2>{c.handle ?? c.displayName} needs comment access</h2>
            <p>Its enabled rules stay paused until the {c.provider === "instagram" ? "Instagram" : "Facebook"} permission is restored.</p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/w/$workspace/channels/$connectionId" params={{ ...params, connectionId: c.id }}>
              Review connection
              <ArrowUpRight />
            </Link>
          </Button>
        </div>
      ))}

      <section>
        <SectionHead title="Rules" description="Phrase rules in priority order. Lower runs first; the first rule that hides wins." />
        {rules.length === 0 ? (
          <div className="auto-template-callout">
            <div>
              <p className="font-medium">No rules yet</p>
              <p className="mt-1 text-sm text-muted-foreground">{channels.connections.length === 0 ? "Connect a channel first." : "Create one to hide, tag or flag comments automatically."}</p>
            </div>
            {manage && channels.connections.length > 0 && (
              <Button variant="outline" asChild>
                <Link to="/w/$workspace/automations/moderation/$ruleId" params={{ ...params, ruleId: "new" }}>
                  <Plus />
                  Create rule
                </Link>
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20 pl-5">Priority</TableHead>
                  <TableHead>Rule</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10">
                    <span className="sr-only">Edit</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="pl-5 tabular-nums text-muted-foreground">{r.priority}</TableCell>
                    <TableCell className="py-4">
                      <div className="flex items-center gap-3">
                        <span className="flex size-9 items-center justify-center rounded-lg border bg-muted/40">
                          <ShieldCheck className="size-4" />
                        </span>
                        <div>
                          <Link className="font-medium hover:underline" to="/w/$workspace/automations/moderation/$ruleId" params={{ ...params, ruleId: r.id }}>
                            {r.name}
                          </Link>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {r.accountHandle} · revision {r.revision} · {r.phrases.length} phrase{r.phrases.length === 1 ? "" : "s"}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{[r.actions.hide && "Hide", r.actions.tagName && `Tag ${r.actions.tagName}`, r.actions.notify && "Notify"].filter(Boolean).join(" · ") || "No action"}</TableCell>
                    <TableCell>
                      <DotBadge warn={r.effectiveStatus === "paused_access_missing"}>{STATUS[r.effectiveStatus] ?? r.effectiveStatus}</DotBadge>
                    </TableCell>
                    <TableCell>
                      <Button size="icon" variant="ghost" aria-label={`Edit ${r.name}`} asChild>
                        <Link to="/w/$workspace/automations/moderation/$ruleId" params={{ ...params, ruleId: r.id }}>
                          <ChevronRight />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>


      <section className="mt-9">
        <SectionHead title="Classifier" description="Reads every comment and scores it. It cannot act; rules decide. Its suggestions are collected below so you can judge it before any rule is allowed to use it." />
      <div className="auto-two-col">
        <Card>
          <CardHeader>
            <CardTitle>What the classifier should know about your brand</CardTitle>
            <CardDescription>Not a rule. A rule says what happens to a comment; this text only helps the classifier read comments the way your team would, so its scores fit your brand. If it ever disagrees with a rule, the rule wins, because only rules act.</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} disabled={!manage} maxLength={4000} className="min-h-40" aria-label="Brand context for the classifier" placeholder="Example: We sell handmade ceramics. Complaints about orders are customers to help, never abuse. Accounts selling followers or refund-recovery services are scams. Our customers write in English and German." />
          </CardContent>
          <CardFooter className="justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              Revision {policy.revision}
              {policy.updatedByName && policy.updatedAt ? ` · ${policy.updatedByName}, ${formatDateTime(policy.updatedAt)}` : ""}
              {" · "}Saving a change re-reads unresolved comments from the last seven days.
            </p>
            {manage && (
              <Button onClick={() => save.mutate({ text, monthlyTokenBudget: Number(budget) || policy.monthlyTokenBudget })} disabled={save.isPending || !dirty}>
                Save
              </Button>
            )}
          </CardFooter>
        </Card>
        <Card className="self-start">
          <CardHeader>
            <CardTitle>Controls</CardTitle>
            <CardDescription>{providerLine} Scores are risk signals, not certainties.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium">Pause all moderation</p>
                <p className="mt-0.5 text-xs text-muted-foreground">The kill switch: every rule and every assessment stops at once.</p>
              </div>
              <Switch checked={policy.paused} disabled={!manage || save.isPending} onCheckedChange={(paused) => save.mutate({ paused })} aria-label="Pause all moderation" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="policy-budget">Monthly token budget</Label>
              <Input id="policy-budget" type="number" min={0} value={budget} onChange={(e) => setBudget(e.target.value)} disabled={!manage} />
              <Progress value={usedPct} aria-label="Budget used this month" />
              <p className="text-xs text-muted-foreground">
                {policy.tokensUsedThisMonth.toLocaleString()} of {policy.monthlyTokenBudget.toLocaleString()} used this month ({usedPct}%). At the budget, assessments stop and comments stay visible.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      </section>

      <section className="mt-9">
        <SectionHead title="What the classifier would have done" description="Shadow mode: every suggestion is recorded and nothing is executed. This is the evidence for deciding which rules may act later." />
        {suggestions.length === 0 ? (
          <Note>No suggestions yet. They appear here as comments are assessed{policy.provider === "off" ? ", once assessments are switched on for this deployment" : ""}.</Note>
        ) : (
          <div className="overflow-hidden rounded-xl border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5">When</TableHead>
                  <TableHead>Comment</TableHead>
                  <TableHead>Suggestion</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Confidence</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suggestions.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="pl-5 whitespace-nowrap text-muted-foreground">{formatDateTime(s.createdAt)}</TableCell>
                    <TableCell className="max-w-md">
                      <Link className="line-clamp-2 hover:underline" {...commentLink(s.commentId)}>
                        {s.commentText ?? "(no text)"}
                      </Link>
                      {s.summary && <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{s.summary}</p>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{SUGGESTION[s.decision] ?? s.decision}</Badge>
                    </TableCell>
                    <TableCell>{CATEGORY_LABELS[s.category]}</TableCell>
                    <TableCell>{s.riskLevel === "high" ? "High" : "Uncertain"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <section className="mt-9">
        <SectionHead title="Rule activity" description="Every action a rule took, linked to the comment it acted on." />
        {activity.length === 0 ? (
          <Note>No rule has acted yet.</Note>
        ) : (
          <div className="overflow-hidden rounded-xl border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-5">When</TableHead>
                  <TableHead>Comment</TableHead>
                  <TableHead>Rule</TableHead>
                  <TableHead>Outcome</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activity.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="pl-5 whitespace-nowrap text-muted-foreground">{formatDateTime(a.createdAt)}</TableCell>
                    <TableCell className="max-w-md">
                      <Link className="line-clamp-2 hover:underline" {...commentLink(a.commentId)}>
                        {a.commentText ?? "(no text)"}
                      </Link>
                    </TableCell>
                    <TableCell>{a.ruleName}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{a.outcome}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </div>
  )
}
